import React, { useEffect, useMemo, useState } from "react";
import { LegendKey } from "../../models/chart_model";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { useDispatch } from "react-redux";
import { COST_UNIT } from "../../Units";
import { normalizeDisplayLabel } from "../../utility/displayText";

// Interface for the calculated statistics, including extra info for min and max.
export interface LegendStats {
  name: string;
  min: number;
  max: number;
  avg: number;
  total: number;
  minExtra: Record<string, any>;
  maxExtra: Record<string, any>;
  // Optional price fields:
  minPrice?: number;
  maxPrice?: number;
  wtdAvg?: number;
}

export interface LegendSummaryTableProps {
  legends: LegendKey[];
  data: any[];
  block_key?: string;
  tableData?: any[]; // Optional: data to be displayed in the table (not used here)
  unit?: "mw" | "mwh" | "mus" | string;
  extraKeys: string[]; // e.g. ["time_block", "date"]
  extraLabels: string[]; // e.g. ["Time Block", "Date"]
  itemsPerPage?: number; // Optional: number of items per page (not used here)
  priceLegends?: LegendKey[]; // Optional: price legends matching legends by index
  filename?: string; // Optional: filename for CSV download
  showTotal?: boolean; // Optional: whether to show the total column
  showWtdAvg?: boolean; // Optional: whether to show weighted average
  showTotalEmpty?: boolean; // Optional: whether to show total as empty when no data
  defaultSortKey?: string; // Optional: default sort key for ChartTable
  isSmall?: boolean; // Optional: whether to use smaller styling for compact layouts
  // Server-side pagination (opt-in, all optional for backward compatibility)
  serverSidePagination?: boolean;
  totalCount?: number;
  currentServerPage?: number;
  onServerPageChange?: (page: number) => void;
  onServerItemsPerPageChange?: (size: number) => void;
  serverLoading?: boolean;
  serverTotalPages?: number;
  tableHeight?: string; // Optional: custom height for ChartTable container
}

/**
 * Calculate summary statistics for each legend along with extra information from the data record
 * where the min and max values occurred.
 */
export function calculateLegendStats(
  unit: string,
  legends: LegendKey[],
  data: any[],
  extraKeys: string[] = [],
  tableData: any[] = [],
  showWtdAvg: boolean = false,
): LegendStats[] {
  function calculateLegends(legend: any) {
    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const cleanName = normalizeDisplayLabel(legend.name).replace(/\s*\([^)]*\)\s*$/, "").trim();
    return `${cleanName} (${suffix})`;
  }

  return legends.map((legend) => {
    const { dataKey } = legend;
    let minVal = Infinity;
    let maxVal = -Infinity;
    let total = 0;
    let avgTotal = 0;
    let count = 0;
    let minRecord: any = null;
    let maxRecord: any = null;

    // Weighted average calculations
    let wtdAvgTotal = 0;
    let wtdAvgCount = 0;

    data.forEach((item) => {
      const value = Number(item[dataKey]);

      if (isNaN(value)) return;

      if (value < minVal) {
        minVal = value;
        minRecord = item;
      }
      if (value > maxVal) {
        maxVal = value;
        maxRecord = item;
      }
      avgTotal += value;
      count++;

      // Calculate weighted average if showWtdAvg is true
      // Calculate weighted average if showWtdAvg is true
      if (showWtdAvg) {
        // Only calculate weighted average for Actual data
        if (dataKey === "Actual_" || dataKey.includes("Actual")) {
          // For trend tab data, construct the volume and price keys dynamically
          let volumeKey = "Actual_mcv_mw";
          let priceKey = "Actual_wt_mcp_rs_kwh";

          // If dataKey contains a date pattern (like "2025-07-01 to 2025-07-01_Actual_wt_mcp_rs_kwh")
          if (typeof dataKey === "string" && dataKey.includes("_Actual_")) {
            const datePrefix = dataKey.split("_Actual_")[0];
            volumeKey = `${datePrefix}_Actual_mcv_mw`;
            priceKey = `${datePrefix}_Actual_wt_mcp_rs_kwh`;
          }

          const volume = Number(item[volumeKey]) || 0;
          const weightedPrice = Number(item[priceKey]) || 0;

          if (volume > 0 && weightedPrice > 0) {
            wtdAvgTotal += volume * weightedPrice; // sum of (mcv * wt_mcp)
            wtdAvgCount += volume; // sum of mcv (total volume)
          }
        }
        // For Forecast data, we don't calculate weighted average - it will remain 0
      }
    });

    let divideBy = 4;

    tableData.forEach((item) => {
      const value = Number(item[dataKey]);
      if (isNaN(value)) return;
      total += value;
    });

    if (typeof dataKey === "string" && dataKey.endsWith("_mw")) {
      total = total / divideBy;
    }

    const avg = count > 0 ? avgTotal / count : 0;
    const wtdAvg =
      showWtdAvg && wtdAvgCount > 0 ? wtdAvgTotal / wtdAvgCount : 0;

    const minExtra: Record<string, any> = {};
    const maxExtra: Record<string, any> = {};
    extraKeys.forEach((key) => {
      if (key === "Date" && legend.dateKey) {
        minExtra[key] = minRecord ? minRecord[legend.dateKey] : "-";
        maxExtra[key] = maxRecord ? maxRecord[legend.dateKey] : "-";
      } else {
        const resolvedKey =
          key === "block" && legend.blockKey ? legend.blockKey : key;
        minExtra[key] = minRecord ? (minRecord[resolvedKey] ?? "-") : "-";
        maxExtra[key] = maxRecord ? (maxRecord[resolvedKey] ?? "-") : "-";
      }
    });

    return {
      name: calculateLegends(legend),
      min: count > 0 ? minVal : 0,
      max: count > 0 ? maxVal : 0,
      avg,
      total,
      minExtra,
      maxExtra,
      unit: legend.unit,
      wtdAvg: showWtdAvg
        ? dataKey === "Actual_" || dataKey.includes("Actual")
          ? wtdAvg
          : undefined
        : undefined,
    };
  });
}

/**
 * A component that displays a summary table of legend statistics.
 * In addition to the regular columns (Min, Max, Avg, Total), if priceLegends are provided,
 * the Min and Max groups will each display two main columns: one for the "Mu" (value)
 * and one for "Price", followed by extra sub-columns.
 *
 * For example, if extraKeys is ["time_block","date"], the sub-headers for the Min group will be:
 *   Mu | Price | Time Block | Date
 *
 * The component computes the price values by scanning the data for the corresponding price legend key
 * (matched by the same index in the priceLegends array).
 */
const LegendSummaryTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit = "MWh",
  extraKeys,
  tableData = [],
  extraLabels,
  block_key,
  priceLegends,
  showTotal = true,
  showWtdAvg = false,
  showTotalEmpty = false,
}) => {
  // Pair each extra key with its label.
  const extras = extraKeys.map((key, idx) => ({
    key,
    label: extraLabels[idx] || key,
  }));
  const dispatch = useDispatch();

  // Calculate basic stats for the legends.
  const stats = calculateLegendStats(
    unit,
    legends,
    data,
    extraKeys,
    tableData,
    showWtdAvg,
  );

  // If priceLegends are provided, compute minPrice and maxPrice for each legend.
  const statsWithPrice = stats.map((stat, index) => {
    if (priceLegends && priceLegends[index]) {
      const priceKey = priceLegends[index].dataKey;
      let minPrice = Infinity;
      let maxPrice = -Infinity;
      data.forEach((item) => {
        const price = Number(item[priceKey]);
        if (!isNaN(price)) {
          if (price < minPrice) minPrice = price;
          if (price > maxPrice) maxPrice = price;
        }
      });

      return {
        ...stat,
        minPrice: minPrice === Infinity ? 0 : minPrice,
        maxPrice: maxPrice === -Infinity ? 0 : maxPrice,
      };
    }

    dispatch(setLoadingFilterName(""));
    return stat;
  });

  // Adjust total if unit is 'mw': convert total to MWh by dividing by 4.

  const adjustedStats = statsWithPrice.map((stat) => ({
    ...stat,
  }));

  // Determine the label for numeric values.
  const valueUnitLabel = unit === "MW" ? "MW" : unit === "MWh" ? "MWh" : unit;

  // Determine how many sub-columns each group should have.
  // If priceLegends exist, then for each group (Min/Max) we have: "Mu", "Price", plus the extra columns.
  const groupColumnCount = (priceLegends ? 2 : 1) + extras.length;

  return (
    <div className="overflow-x-auto capitalize tab-animatable-content">
      <table className="min-w-full text-nowrap ">
        <thead className="sticky top-0 z-10 ">
          {/* First header row: group labels */}
          <tr className="relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-[#fdd3bb]">
            <th
              rowSpan={2}
              className="py-2 px-6 text-center z-10 relative after:content-[''] border-0 after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Name
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Minimum
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Maximum
            </th>
            <th
              rowSpan={2}
              className={`py-2 px-6 text-center z-10 relative border-0 ${
                showTotal === false && showWtdAvg === false
                  ? ""
                  : "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              }`}
            >
              Average
            </th>
            {showTotal && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Total
              </th>
            )}
            {showWtdAvg && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Wt. Avg
              </th>
            )}
          </tr>
          {/* Second header row: sub-columns */}
          <tr className="text-gray-700">
            {/* Min group sub-columns */}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {priceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceLegends[0].unit}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`min-extra-header-${idx}`}
                className="py-2 px-6 text-center capitalize border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
            {/* Max group sub-columns */}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {priceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceLegends[0].unit}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`max-extra-header-${idx}`}
                className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-[#8C8A96] font-normal text-[13px]">
          {adjustedStats.map((stat, index) => (
            <tr
              key={index}
              className="border-b rounded-xl border-gray-200 hover:bg-gray-100 text-center"
            >
              <td className="py-3 px-6 text-center ">
                {(() => {
                  // Parse the name to find unit in parentheses
                  const match = stat.name.match(/(.*)\s*\((.*?)\)$/);
                  if (match) {
                    const [_, nameText, unitText] = match;
                    return (
                      <>
                        <span>{nameText}</span>{" "}
                        <span className="text-[#34656D] font-medium">
                          ({unitText})
                        </span>
                      </>
                    );
                  } else {
                    // If no unit pattern found, just return the whole name
                    return stat.name;
                  }
                })()}
              </td>
              {/* Min group: Mu column */}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.min.toFixed(2)}
              </td>
              {/* Min group: Price column */}
              {priceLegends && (
                <td className="py-3 px-6 ">
                  {stat.minPrice !== undefined ? stat.minPrice.toFixed(2) : "-"}
                </td>
              )}
              {/* Min group: Extra columns */}
              {extras.map((extra, idx) => {
                return (
                  <td
                    key={`min-extra-${index}-${idx}`}
                    className={`py-3 px-6 ${
                      extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                    }`}
                  >
                    {stat.minExtra[extra.key] !== undefined
                      ? stat.minExtra[extra.key]
                      : "-"}
                  </td>
                );
              })}
              {/* Max group: Mu column */}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.max.toFixed(2)}
              </td>
              {/* Max group: Price column */}
              {priceLegends && (
                <td className="py-3 px-6">
                  {stat.maxPrice !== undefined ? stat.maxPrice.toFixed(2) : "-"}
                </td>
              )}
              {/* Max group: Extra columns */}
              {extras.map((extra, idx) => (
                <td
                  key={`min-extra-${index}-${idx}`}
                  className={`py-3 px-6 ${
                    extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                  }`}
                >
                  {stat.maxExtra[extra.key] !== undefined
                    ? stat.maxExtra[extra.key]
                    : "-"}
                </td>
              ))}
              <td className="py-3 px-6">
                <span className=" font-medium">{stat.avg.toFixed(2)}</span>
              </td>
              {showTotal && (
                <td className="py-3 px-6">
                  {(() => {
                    const match = stat.name.match(/.*\((.*?)\)$/);
                    let unit = match ? match[1] : "";

                    // If it's a percentage, rate (contains Rs), or the total is NaN, show "-"
                    if (
                      isNaN(stat.total) ||
                      unit === "%" ||
                      unit.includes("Rs") ||
                      unit.includes("₹") ||
                      stat.name.includes("%") ||
                      stat.name.toLowerCase().includes("percent")
                    ) {
                      return <span>-</span>;
                    }

                    if (unit.toLowerCase() === "mw") {
                      unit = "MWh";
                    }
                    return (
                      <span className="font-medium">
                        {showTotalEmpty ? (
                          <span>-</span>
                        ) : (
                          <>
                            {" "}
                            {stat.total.toFixed(2)} {" ("}
                            {unit}
                            {")"}
                          </>
                        )}
                      </span>
                    );
                  })()}
                </td>
              )}
              {showWtdAvg && (
                <td className="py-3 px-6">
                  <span className="font-medium">
                    {stat.wtdAvg === null
                      ? "-"
                      : stat.wtdAvg !== undefined
                        ? stat.wtdAvg.toFixed(2)
                        : "-"}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {priceLegends && (
        <div className="text-center text-sm text-gray-500 mt-4 ">
          The Min and Max {priceLegends[0].unit} is corresponding to the Min and
          Max {extraKeys.join(", ")} of {valueUnitLabel}
        </div>
      )}
    </div>
  );
};

export default LegendSummaryTable;

/**
 * Downloads the summary data in CSV format.
 *
 * @param legends - Array of legend configurations.
 * @param data - Array of data objects.
 * @param unit - Unit of measurement ("mw" or "mwh"). If "mw", total is converted to MWh.
 * @param extraKeys - Array of keys to extract extra info from the data records.
 * @param extraLabels - Array of labels corresponding to the extra keys.
 */
export function downloadSummaryCSV({
  legends,
  data,
  unit = "MWh",
  tableData,
  extraKeys,
  extraLabels,
  filename = "summary.csv",
}: LegendSummaryTableProps): void {
  // Calculate summary statistics using the same logic as in LegendSummaryTable.
  const stats = calculateLegendStats(unit, legends, data, extraKeys, tableData);

  // If unit is "mw", convert the total to MWh (divide by 4); otherwise, leave as is.
  const adjustedStats = stats.map((stat) => ({
    ...stat,
  }));

  // Determine the label for the numeric values.
  const valueUnitLabel = unit === "mw" ? "MW" : unit === "mwh" ? "MWh" : "MUs";

  // Construct the CSV header.
  // Columns: Name, Min (unit), [for each extra label: Min - {label}], Max (unit), [for each extra label: Max - {label}], Avg (unit), Total (MWh)
  const headers = [
    "Name",
    `Min (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Min - ${label}`),
    `Max (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Max - ${label}`),
    `Avg (${valueUnitLabel})`,
    `Total (${unit === "mw" ? "MWh" : unit === "mwh" ? "MWh" : "MUs"})`,
  ];

  // Build CSV rows for each legend summary.
  const rows = adjustedStats.map((stat) => {
    const row = [];
    row.push(stat.name);
    row.push(stat.min.toFixed(2));
    // Append each extra value for the min record
    for (let i = 0; i < extraKeys.length; i++) {
      row.push(stat.minExtra[extraKeys[i]]);
    }
    row.push(stat.max.toFixed(2));
    // Append each extra value for the max record
    for (let i = 0; i < extraKeys.length; i++) {
      row.push(stat.maxExtra[extraKeys[i]]);
    }
    row.push(stat.avg.toFixed(2));
    row.push(stat.total.toFixed(2));
    return row;
  });

  // Combine headers and rows into a CSV string.
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  // Create a Blob from the CSV string and trigger a download.
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + " Summary Data");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

type SortConfig = {
  // When composite is true, data is sorted by extraKeys[0] (and extraKeys[1] if available)
  composite: boolean;
  key: string | null;
  direction: "asc" | "desc";
};

export const ChartTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
  block_key,
  itemsPerPage: itemsPerPageProp,
  defaultSortKey: defaultSortKeyProp,
  isSmall = false,
  serverSidePagination = false,
  totalCount = 0,
  currentServerPage = 1,
  onServerPageChange,
  onServerItemsPerPageChange,
  serverLoading = false,
  serverTotalPages,
  tableHeight,
}) => {
  const dispatch = useDispatch();
  const defaultSortKey =
    defaultSortKeyProp ||
    (extraKeys.length > 0 ? extraKeys[extraKeys.length - 1] : "");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    composite: false,
    key: defaultSortKey,
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageProp || 96,
  );
  const [jumpValue, setJumpValue] = useState<string>("");

  // Reset to page 1 whenever the incoming data changes (e.g. filter applied)
  useEffect(() => {
    if (!serverSidePagination) {
      setCurrentPage(1);
    }
  }, [data, serverSidePagination]);

  const effectivePage = serverSidePagination ? currentServerPage : currentPage;

  const handlePageSet = (page: number) => {
    if (serverSidePagination && onServerPageChange) {
      onServerPageChange(page);
    } else {
      setCurrentPage(page);
    }
  };

  const handleSort = (columnKey: string) => {
    if (sortConfig.composite || sortConfig.key !== columnKey) {
      setSortConfig({ composite: false, key: columnKey, direction: "desc" });
    } else {
      setSortConfig({
        composite: false,
        key: columnKey,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    }
    // Only reset page for client-side pagination; server-side sorts current page in place
    if (!serverSidePagination) {
      handlePageSet(1);
    }
  };

  const MONTH_ABBR = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parseMonthStr = (v: string): number | null => {
    const m = /^([A-Za-z]{3})-(\d{2})$/.exec(v);
    if (!m) return null;
    const mon = MONTH_ABBR.findIndex(
      (x) => x.toLowerCase() === m[1].toLowerCase(),
    );
    if (mon === -1) return null;
    return parseInt(m[2]) * 12 + mon;
  };

  const parseDDMMYYYY = (v: string): number | null => {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (!m) return null;
    return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime();
  };

  const compareValues = (aValue: any, bValue: any): number => {
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;
    if (typeof aValue === "number" && typeof bValue === "number") {
      return aValue - bValue;
    }
    const aStr = aValue.toString();
    const bStr = bValue.toString();
    const aMonth = parseMonthStr(aStr);
    const bMonth = parseMonthStr(bStr);
    if (aMonth !== null && bMonth !== null) {
      return aMonth - bMonth;
    }
    const aDate = parseDDMMYYYY(aStr);
    const bDate = parseDDMMYYYY(bStr);
    if (aDate !== null && bDate !== null) {
      return aDate - bDate;
    }
    return aStr.localeCompare(bStr);
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortConfig.composite) {
        let cmp = compareValues(a[extraKeys[0]], b[extraKeys[0]]);
        if (cmp === 0 && extraKeys.length > 1) {
          cmp = compareValues(a[extraKeys[1]], b[extraKeys[1]]);
        }
        return -cmp;
      } else if (sortConfig.key) {
        let cmp = compareValues(a[sortConfig.key], b[sortConfig.key]);
        return sortConfig.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig, extraKeys]);

  const totalPages = serverSidePagination
    ? (serverTotalPages ?? Math.ceil(totalCount / itemsPerPage))
    : Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    if (serverSidePagination) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [serverSidePagination, sortedData, currentPage, itemsPerPage]);

  const renderSortArrow = (columnKey: string) => {
    if (!sortConfig.composite && sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  function calculateLegends(legend: any) {
    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const cleanName = normalizeDisplayLabel(legend.name).replace(/\s*\([^)]*\)\s*$/, "").trim();
    return `${cleanName} (${suffix})`;
  }

  const headerColumns = [
    ...extraKeys.map((key, index) => ({
      key,
      label: normalizeDisplayLabel(extraLabels[index] || key),
    })),
    ...legends.map((legend) => ({
      key: legend.dataKey,
      // label: `${legend.name} (${unit?.toUpperCase() ?? legend.unit})`,
      label: calculateLegends(legend),
    })),
  ];

  const handlePreviousPage = () => {
    if (effectivePage > 1) handlePageSet(effectivePage - 1);
  };

  const handleNextPage = () => {
    if (effectivePage < totalPages) handlePageSet(effectivePage + 1);
  };

  const handleJump = () => {
    const page = parseInt(jumpValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      handlePageSet(page);
    }
    setJumpValue("");
  };

  const handleItemsPerPageChange = (newSize: number) => {
    setItemsPerPage(newSize);
    if (serverSidePagination && onServerItemsPerPageChange) {
      onServerItemsPerPageChange(newSize);
    }
    handlePageSet(1);
  };

  // Always returns exactly 7 slots so the layout never shifts.
  const getPageButtons = (): (number | "..." | null)[] => {
    const SLOTS = 7;
    if (totalPages <= SLOTS) {
      return [
        ...Array.from({ length: totalPages }, (_, i) => i + 1),
        ...Array<null>(SLOTS - totalPages).fill(null),
      ] as (number | null)[];
    }
    if (effectivePage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (effectivePage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      effectivePage - 1,
      effectivePage,
      effectivePage + 1,
      "...",
      totalPages,
    ];
  };

  useEffect(() => {
    dispatch(setLoadingFilterName(""));
  }, [dispatch]);

  return (
    <div
      className={`chartTable ${isSmall ? "isSmall" : ""} flex flex-col tab-animatable-content`}
      style={
        isSmall ? undefined : { height: tableHeight ?? "calc(100vh - 315px)" }
      }
    >
      {/* In small mode, avoid inner vertical scrolling to prevent double scrollbars */}
      <div
        className={`${isSmall ? "overflow-x-auto overflow-y-visible" : "flex-1 min-h-0 overflow-auto text-nowrap"} capitalize`}
      >
        <table className={`${isSmall ? "w-full" : ""}`}>
          <thead className="sticky top-0 z-10">
            <tr className={isSmall ? "text-[10px]" : "text-sm"}>
              {headerColumns.map((col, index) => {
                return (
                  <th
                    key={index}
                    className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} cursor-pointer text-center`}
                    onClick={() => handleSort(col.key)}
                  >
                    <div className="flex items-center justify-center">
                      {(() => {
                        // Parse the label to find unit in parentheses
                        const match = col.label.match(
                          /(.*)\s*\((MW|MWH|MUS|Rs\/kWh|LU|₹\/kWh|Rs|%)\)$/i,
                        );

                        if (match) {
                          const [_, nameText, unitText] = match;

                          // Determine color for unit
                          let unitColorClass = "";

                          unitColorClass = "text-[#34656D]";

                          return (
                            <>
                              <span className="font-medium">{nameText} </span>
                              <span
                                className={`${unitColorClass} ms-1 font-medium`}
                              >
                                ({unitText})
                              </span>
                              <span className="font-medium"></span>
                            </>
                          );
                        } else {
                          // If no unit pattern found, just return the whole label
                          return <span>{col.label.replace("()", "")}</span>;
                        }
                      })()}
                      <span className="ml-1">{renderSortArrow(col.key)}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody
            className={`text-[#8C8A96] font-normal ${isSmall ? "text-[11px]" : "text-[13px]"}`}
          >
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-gray-200 hover:bg-gray-100 text-center"
              >
                {extraKeys.map((key, index) => {
                  return (
                    <td
                      key={index}
                      className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} text-center ${
                        key === "block" ? "text-[#F1935C] font-medium" : ""
                      }`}
                    >
                      {row[key] !== undefined
                        ? typeof row[key] === "string"
                          ? normalizeDisplayLabel(row[key])
                          : row[key]
                        : "-"}
                    </td>
                  );
                })}
                {legends.map((legend, index) => (
                  <td
                    key={index}
                    className={`${isSmall ? "py-1 px-2" : "py-3 px-4"}`}
                  >
                    {legend.deviationValueKey &&
                    row[legend.deviationValueKey] != null
                      ? `${formatCellValue(row[legend.dataKey])} (${formatCellValue(row[legend.deviationValueKey])})`
                      : formatCellValue(row[legend.dataKey])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Loading overlay for server-side pagination */}
      {serverLoading && (
        <div className="absolute inset-0 bg-white/60 z-30 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#f1935c] border-t-transparent rounded-full" />
        </div>
      )}

      {/* ── Pagination bar ── */}

      <div
        className={`flex flex-wrap items-center justify-between gap-2 shrink-0 bg-white border-t border-gray-200 z-20 px-2 ${
          isSmall ? "text-[10px] py-1" : "text-sm py-2"
        }`}
      >
        {/* Left: record-range info */}
        <span className="text-gray-500 whitespace-nowrap w-[300px]">
          {(() => {
            const total = serverSidePagination ? totalCount : sortedData.length;
            if (total === 0) return "No records";
            return `${(effectivePage - 1) * itemsPerPage + 1}–${Math.min(
              effectivePage * itemsPerPage,
              total,
            )} of ${total}`;
          })()}
        </span>

        {/* Centre: First / prev / page-numbers / next / last */}
        <div className="flex items-center gap-1">
          {/* First */}
          <button
            onClick={() => handlePageSet(1)}
            disabled={effectivePage === 1}
            title="First page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            «
          </button>

          {/* Previous */}
          <button
            onClick={handlePreviousPage}
            disabled={effectivePage === 1}
            title="Previous page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            ‹
          </button>

          {/* Page number buttons — always 7 fixed slots to prevent layout shift */}
          {getPageButtons().map((btn, idx) =>
            btn === null ? (
              // invisible placeholder — holds the same space as a real button
              <span
                key={`empty-${idx}`}
                aria-hidden
                className={`inline-block ${
                  isSmall ? "min-w-5 h-5" : "min-w-7 h-7"
                } invisible`}
              />
            ) : btn === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className={`inline-flex items-center justify-center select-none ${
                  isSmall ? "min-w-5 h-5 text-[10px]" : "min-w-7 h-7 text-xs"
                }`}
              >
                …
              </span>
            ) : (
              <button
                key={btn}
                onClick={() => handlePageSet(btn as number)}
                className={`${
                  isSmall ? "min-w-5 h-5 px-1 text-[10px]" : "min-w-7 h-7 px-1 text-xs"
                } border rounded ${
                  effectivePage === btn
                    ? "bg-[#f1935c] text-white border-[#f1935c] font-semibold"
                    : "hover:bg-gray-100"
                }`}
              >
                {btn}
              </button>
            ),
          )}

          {/* Next */}
          <button
            onClick={handleNextPage}
            disabled={effectivePage === totalPages || totalPages === 0}
            title="Next page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            ›
          </button>

          {/* Last */}
          <button
            onClick={() => handlePageSet(totalPages)}
            disabled={effectivePage === totalPages || totalPages === 0}
            title="Last page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            »
          </button>
        </div>

        {/* Right: jump-to-page + rows-per-page */}
        <div
          style={{ paddingRight: "5rem" }}
          className="flex items-center gap-3"
        >
          {/* Jump to page */}
          <div className="flex items-center gap-1">
            <span className="text-gray-500 whitespace-nowrap">Go to</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJump()}
              placeholder="#"
              className={`${
                isSmall ? "w-10 text-[10px] py-0.5" : "w-14 text-xs py-1"
              } border rounded px-1 text-center outline-none focus:border-[#f1935c]`}
            />
            <button
              onClick={handleJump}
              className={`${
                isSmall ? "px-1 py-0.5 text-[10px]" : "px-2 py-1 text-xs"
              } border rounded bg-[#f1935c] text-white hover:bg-[#f1935c]`}
            >
              Go
            </button>
          </div>

          {/* Rows per page */}
          <div className="flex items-center gap-1">
            <span className="text-gray-500 whitespace-nowrap">Rows</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className={`!h-7 ${
                isSmall ? "text-[10px] py-0.5" : "text-xs py-1"
              } border rounded px-1 outline-none focus:border-[#f1935c] cursor-pointer`}
            >
              {[25, 50, 96, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatCellValue = (value: any): string => {
  if (value === undefined || value === null) return "-";
  // If the original value is a string, return as-is (preserves block labels etc.)
  if (typeof value === "string") return value;
  const num = Number(value);
  // If value is numeric, format it.
  if (!isNaN(num)) {
    return num.toFixed(2);
  }
  return value.toString();
};

/**
 * Downloads the summary data (displayed in ChartTable/LegendSummaryTable) as a CSV file.
 * The CSV columns are built as follows:
 *   - The first columns come from extraKeys (labeled using extraLabels)
 *   - The remaining columns come from legends (header includes the legend name and unit)
 *
 * @param legends - Array of legend configurations.
 * @param data - Array of data objects.
 * @param unit - Unit of measurement ("mw" or "mwh").
 * @param extraKeys - Keys to display as the first columns.
 * @param extraLabels - Corresponding labels for the extra keys.
 */
export function downloadChartTableCSV({
  filename = "chart_table.csv",
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
}: LegendSummaryTableProps): void {
  // Build the header row:
  // Use extraLabels for the extra columns, and for each legend add "legendName (UNIT)"

  function calculateLegends(legend: any) {
    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const cleanName = normalizeDisplayLabel(legend.name).replace(/\s*\([^)]*\)\s*$/, "").trim();
    return `${cleanName} (${suffix})`;
  }
  const headers = [
    ...extraKeys.map((key, idx) => extraLabels[idx] || key),
    ...legends.map((legend) => calculateLegends(legend)),
  ];

  // Build CSV rows from each data object.
  // For each row, first output values for extraKeys, then for each legend's dataKey.
  const formatNum = (v: any) => {
    if (v === undefined || v === null || v === "") return "";
    const n = Number(v);
    return isNaN(n) ? String(v) : n.toFixed(2);
  };

  const rows = data.map((row) => {
    const extraValues = extraKeys.map((key) => {
      const value = row[key] !== undefined ? row[key] : "";
      return typeof value === "string" && value.includes(",")
        ? `"${value}"`
        : value;
    });
    const legendValues = legends.map((legend) => {
      const raw = row[legend.dataKey];
      if (raw === undefined || raw === null) return "";
      const formatted = formatNum(raw);
      if (legend.deviationValueKey && row[legend.deviationValueKey] != null) {
        return `"${formatted} (${formatNum(row[legend.deviationValueKey])})"`;
      }
      return formatted;
    });
    return [...extraValues, ...legendValues];
  });

  // Combine header and rows into a CSV string.
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  // Create a Blob and trigger a download.
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + " Chart Data");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
